import React, { useRef, useState, useCallback } from 'react';

const BACKEND    = 'https://trailcode-backend.onrender.com';
const ENGINE_CDN = 'https://esm.sh/@lwc/engine-dom@9.0.3';

// ── Convert PascalCase to kebab-case tag name ──────────────────────────────────
function toTagName(n) {
  return 'c-' + n.replace(/([A-Z])/g, m => '-' + m.toLowerCase()).replace(/^-/, '');
}

// ── Fake lightning-* base components ──────────────────────────────────────────
const LIGHTNING_COMPONENTS = `
  customElements.define('lightning-card', class extends HTMLElement {
    connectedCallback() {
      const title = this.getAttribute('title') || '';
      const icon  = this.getAttribute('icon-name') || '';
      const inner = this.innerHTML;
      this.innerHTML = \`
        <div style="border:1px solid #dddbda;border-radius:4px;background:#fff;overflow:hidden">
          \${title ? \`<div style="padding:12px 16px;border-bottom:1px solid #dddbda;display:flex;align-items:center;gap:8px">
            \${icon ? \`<span style="font-size:16px">⚡</span>\` : ''}
            <h2 style="margin:0;font-size:16px;font-weight:600;color:#080707">\${title}</h2>
          </div>\` : ''}
          <div style="padding:16px">\${inner}</div>
        </div>\`;
    }
  });

  customElements.define('lightning-badge', class extends HTMLElement {
    connectedCallback() {
      const label   = this.getAttribute('label') || '';
      const variant = this.getAttribute('variant') || 'default';
      const colors  = { success:'#2e844a:#EAF3DE', error:'#ba0517:#FCEBEB', warning:'#a56300:#FAEEDA', inverse:'#fff:#032d60' };
      const [col, bg] = (colors[variant] || '#444:#e5e5e5').split(':');
      this.innerHTML = \`<span style="background:\${bg};color:\${col};border-radius:12px;padding:2px 8px;font-size:12px;font-weight:500">\${label}</span>\`;
    }
  });

  customElements.define('lightning-spinner', class extends HTMLElement {
    connectedCallback() {
      const label = this.getAttribute('alternative-text') || 'Loading';
      this.innerHTML = \`<div style="display:flex;align-items:center;gap:8px;padding:12px;color:#0176d3;font-size:13px">
        <div style="width:20px;height:20px;border:3px solid #e5e5e5;border-top-color:#0176d3;border-radius:50%;animation:spin 0.6s linear infinite"></div>
        \${label}
        <style>@keyframes spin{to{transform:rotate(360deg)}}</style>
      </div>\`;
    }
  });

  customElements.define('lightning-input', class extends HTMLElement {
    connectedCallback() {
      const label = this.getAttribute('label') || '';
      const type  = this.getAttribute('type')  || 'text';
      const placeholder = this.getAttribute('placeholder') || '';
      const value = this.getAttribute('value') || '';
      this.innerHTML = \`
        <div style="margin-bottom:8px">
          \${label ? \`<label style="display:block;font-size:12px;font-weight:600;color:#3e3e3c;margin-bottom:4px">\${label}</label>\` : ''}
          <input type="\${type}" placeholder="\${placeholder}" value="\${value}"
                 style="border:1px solid #c9c7c5;border-radius:4px;padding:6px 10px;font-size:14px;width:100%" />
        </div>\`;
    }
  });

  customElements.define('lightning-button', class extends HTMLElement {
    connectedCallback() {
      const label   = this.getAttribute('label') || 'Button';
      const variant = this.getAttribute('variant') || 'neutral';
      const bg = variant === 'brand' ? '#0176d3' : variant === 'destructive' ? '#ba0517' : '#fff';
      const col = variant === 'neutral' ? '#0176d3' : '#fff';
      const border = variant === 'neutral' ? '#0176d3' : bg;
      this.innerHTML = \`<button style="background:\${bg};color:\${col};border:1px solid \${border};border-radius:4px;padding:6px 14px;cursor:pointer;font-size:14px">\${label}</button>\`;
    }
  });

  customElements.define('lightning-datatable', class extends HTMLElement {
    connectedCallback() {
      let data = [], columns = [];
      try { data    = JSON.parse(this.getAttribute('data')    || '[]'); } catch(e) {}
      try { columns = JSON.parse(this.getAttribute('columns') || '[]'); } catch(e) {}
      const header = columns.map(c => \`<th style="padding:8px 12px;text-align:left;font-size:12px;font-weight:600;background:#f3f3f3;border-bottom:1px solid #ddd">\${c.label||c.fieldName}</th>\`).join('');
      const rows = data.map(row =>
        \`<tr>\${columns.map(c => \`<td style="padding:8px 12px;font-size:13px;border-bottom:1px solid #f0f0f0">\${row[c.fieldName]||''}</td>\`).join('')}</tr>\`
      ).join('');
      this.innerHTML = \`<table style="width:100%;border-collapse:collapse;border:1px solid #ddd;border-radius:4px;overflow:hidden">
        <thead><tr>\${header}</tr></thead>
        <tbody>\${rows}</tbody>
      </table>\`;
    }
  });
`;

// ── Navigation mock ────────────────────────────────────────────────────────────
const NAVIGATION_MOCK = `
  const NavigationMixin = (Base) => class extends Base {
    [Symbol.for('NavigationMixin.Navigate')](pageRef, replace) {
      const log = document.getElementById('nav-log');
      if (log) {
        log.innerHTML = '<div style="background:#e6f1fb;border:1px solid #0176d3;border-radius:4px;padding:10px;font-size:12px;font-family:monospace;margin-top:8px">' +
          '<b style=\\'color:#0176d3\\'>Navigation intercepted</b><br>' +
          'type: ' + (pageRef.type || '') + '<br>' +
          'recordId: ' + (pageRef.attributes?.recordId || '') + '<br>' +
          'objectApiName: ' + (pageRef.attributes?.objectApiName || '') + '<br>' +
          'actionName: ' + (pageRef.attributes?.actionName || '') +
        '</div>';
      }
      console.log('[TrailCode] Navigation intercepted:', JSON.stringify(pageRef));
    }
  };
  NavigationMixin.Navigate = Symbol.for('NavigationMixin.Navigate');
`;

// ── Toast mock ────────────────────────────────────────────────────────────────
const TOAST_MOCK = `
  const _origDispatch = EventTarget.prototype.dispatchEvent;
  EventTarget.prototype.dispatchEvent = function(event) {
    if (event.type === 'lightning__showtoast' || (event.detail && event.detail.title && event.detail.variant)) {
      const d = event.detail;
      const colors = { success:'#2e844a:#EAF3DE', error:'#ba0517:#FCEBEB', warning:'#a56300:#FAEEDA', info:'#0176d3:#E6F1FB' };
      const [col, bg] = (colors[d.variant] || colors.info).split(':');
      const toast = document.createElement('div');
      toast.setAttribute('id', 'toast-' + d.variant);
      toast.style.cssText = 'position:fixed;bottom:16px;right:16px;background:' + bg + ';border:1px solid ' + col + ';border-radius:6px;padding:12px 16px;max-width:320px;z-index:9999;font-size:13px';
      toast.innerHTML = '<b style="color:' + col + '">' + (d.title || '') + '</b><br><span style="color:#3e3e3c">' + (d.message || '') + '</span>';
      document.body.appendChild(toast);
      setTimeout(() => toast.remove(), 3000);
    }
    return _origDispatch.call(this, event);
  };
`;

// ── LMS mock ──────────────────────────────────────────────────────────────────
const LMS_MOCK = `
  window.__LMS_BUS__ = new EventTarget();
  window.__LMS_SUBS__ = new Map();
  const publish   = (ctx, channel, msg) => window.__LMS_BUS__.dispatchEvent(new CustomEvent(channel, { detail: msg }));
  const subscribe = (ctx, channel, handler) => {
    const fn = (e) => handler(e.detail);
    window.__LMS_BUS__.addEventListener(channel, fn);
    const sub = { channel, fn };
    return sub;
  };
  const unsubscribe = (sub) => { if (sub) window.__LMS_BUS__.removeEventListener(sub.channel, sub.fn); };
  const MessageContext = {};
`;

// ── Build complete srcdoc ──────────────────────────────────────────────────────
function buildSrcdoc(compiledTemplate, compiledJs, componentName, mockData, domChecks) {
  const tagName    = toTagName(componentName);
  const checksJson = JSON.stringify(domChecks || []);

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <style>
    * { box-sizing: border-box; }
    body { margin: 0; padding: 16px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; font-size: 14px; color: #1a1a1a; background: #fff; }
    h1 { font-size: 1.5rem; margin: 0 0 8px; }
    h2 { font-size: 1.25rem; margin: 0 0 8px; }
    h3 { font-size: 1rem; font-weight: 600; margin: 0 0 6px; }
    p  { margin: 0 0 8px; line-height: 1.5; }
    ul, ol { margin: 0 0 8px; padding-left: 20px; }
    button { padding: 6px 14px; background: #0176d3; color: #fff; border: none; border-radius: 4px; cursor: pointer; font-size: 14px; }
    button:hover { background: #014486; }
    input, select, textarea { padding: 6px 10px; border: 1px solid #c9c7c5; border-radius: 4px; font-size: 14px; width: 100%; max-width: 320px; }
    label { display: block; font-weight: 600; margin-bottom: 4px; font-size: 13px; }
    .error-box { background: #fef0f0; border: 1px solid #f87171; border-radius: 6px; padding: 12px; color: #b91c1c; font-family: monospace; font-size: 12px; white-space: pre-wrap; }
  </style>
</head>
<body>
  <div id="lwc-root"></div>
  <script>
    // ── DOM check runner ──
    var __CHECKS__ = ${checksJson};

    function __runChecks() {
      var mountEl = document.getElementById('lwc-root');
      var compEl  = mountEl && mountEl.firstElementChild;
      var queryRoot = (compEl && compEl.shadowRoot) || compEl || document;

      var errBox = document.querySelector('.error-box');
      if (errBox) {
        parent.postMessage({ type: 'lwc-checks', results: [{ description: 'Component renders without error', pass: false, message: errBox.textContent.replace(/^[^a-zA-Z0-9]+/, '') }] }, '*');
        return;
      }

      if (!__CHECKS__ || !__CHECKS__.length) {
        var rendered = !!(compEl && ((compEl.shadowRoot && compEl.shadowRoot.children.length > 0) || compEl.children.length > 0 || (compEl.textContent && compEl.textContent.trim().length > 0)));
        parent.postMessage({ type: 'lwc-checks', results: [{ description: 'Component renders without error', pass: rendered, message: rendered ? null : 'Component did not render any content' }] }, '*');
        return;
      }

      var results = __CHECKS__.map(function(check) {
        try {
          if (check.count !== undefined) {
            var els = queryRoot.querySelectorAll(check.selector);
            var pass = els.length === check.count;
            return { description: check.description, pass: pass, message: pass ? null : 'Expected ' + check.count + ' "' + check.selector + '", found ' + els.length };
          }
          var el = queryRoot.querySelector(check.selector);
          if (check.exists !== undefined) {
            var pass = check.exists ? el !== null : el === null;
            return { description: check.description, pass: pass, message: pass ? null : '"' + check.selector + '" ' + (check.exists ? 'not found' : 'should not exist') };
          }
          if (!el) return { description: check.description, pass: false, message: '"' + check.selector + '" not found' };
          if (check.property === 'textContent') {
            var actual = (el.textContent || '').trim();
            if (check.contains) {
              var pass = actual.indexOf(check.contains) !== -1;
              return { description: check.description, pass: pass, message: pass ? null : 'Should contain "' + check.contains + '", got "' + actual + '"' };
            }
            var pass = actual === check.expected;
            return { description: check.description, pass: pass, message: pass ? null : 'Expected "' + check.expected + '", got "' + actual + '"' };
          }
          if (check.attribute) {
            var actual = el.getAttribute(check.attribute);
            var pass = actual === check.expected;
            return { description: check.description, pass: pass, message: pass ? null : '[' + check.attribute + '] should be "' + check.expected + '", got "' + actual + '"' };
          }
          return { description: check.description, pass: true, message: null };
        } catch(e) { return { description: check.description, pass: false, message: e.message }; }
      });
      parent.postMessage({ type: 'lwc-checks', results: results }, '*');
    }

    window.addEventListener('message', function(e) {
      if (e.data && e.data.type === 'run-checks') {
        setTimeout(__runChecks, 150);
      }
    });
    // Fake lightning-* base components
    ${LIGHTNING_COMPONENTS}
    // Navigation mock
    ${NAVIGATION_MOCK}
    // Toast mock
    ${TOAST_MOCK}
    // LMS mock
    ${LMS_MOCK}
  </script>
  <script type="module">
    try {
      const {
        createElement, LightningElement, registerTemplate,
        registerComponent, parseFragment, freezeTemplate,
        registerDecorators,
      } = await import('${ENGINE_CDN}');

      // Aliases used by Babel-compiled component code
      const _registerDecorators = registerDecorators;
      const _registerComponent  = registerComponent;

      // ── Compiled template ──
      ${compiledTemplate}

      // _tmpl alias must come AFTER __REGISTERED_TMPL is defined above
      const _tmpl = __REGISTERED_TMPL;

      // ── Babel-compiled component JS ──
      ${compiledJs}

      // Mount: Babel output stores the registered component as __lwc_component_class_internal
      if (typeof __lwc_component_class_internal === 'undefined') {
        throw new Error('Component class not found. Make sure your class extends LightningElement.');
      }

      const el = createElement('${tagName}', { is: __lwc_component_class_internal });
      document.getElementById('lwc-root').appendChild(el);

    } catch (err) {
      document.getElementById('lwc-root').innerHTML =
        '<div class="error-box">\u26A0\uFE0F ' + err.message + '</div>';
      window.__lwcError = err.message;
    }
  </script>
</body>
</html>`;
}

// ── LWCPreview component ──────────────────────────────────────────────────────
export default function LWCPreview({
  jsCode, htmlCode, componentName,
  domChecks = [], mockData = null,
  isLwcIntegration = false, sfToken, sfInstance,
  challengeId, userId, colors,
}) {
  const c = colors;
  const iframeRef       = useRef(null);
  const pendingValidate = useRef(false);

  const [srcDoc,     setSrcDoc]     = useState('');
  const [previewKey, setPreviewKey] = useState(0);
  const [results,    setResults]    = useState([]);
  const [validated,  setValidated]  = useState(false);
  const [compiling,  setCompiling]  = useState(false);
  const [checking,   setChecking]   = useState(false);
  const [compileErr, setCompileErr] = useState(null);

  // ── Compile HTML template via backend, then build srcdoc ───────────────────
  const compile = useCallback(async (thenValidate = false) => {
    setCompiling(true);
    setCompileErr(null);
    setValidated(false);
    setResults([]);
    try {
      const res  = await fetch(`${BACKEND}/compile-lwc`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          html: htmlCode,
          js:   jsCode,
          componentName,
          mockData: mockData || null,
        }),
      });
      const data = await res.json();

      if (!data.success) {
        setCompileErr(data.error || 'Template compile error');
        setCompiling(false);
        return;
      }

      // Use Babel-compiled componentCode from backend (fully reactive).
      // Fall back to raw jsCode only if backend returned nothing (old endpoint).
      const compiledJs = data.componentCode || jsCode;

      const doc = buildSrcdoc(data.templateCode || data.code, compiledJs, componentName, mockData, domChecks);
      pendingValidate.current = thenValidate;
      setSrcDoc(doc);
      setPreviewKey(k => k + 1);
    } catch (e) {
      setCompileErr('Could not reach backend: ' + e.message);
    } finally {
      setCompiling(false);
    }
  }, [htmlCode, jsCode, componentName, mockData, domChecks]);

  // ── Listen for check results from iframe ──────────────────────────────────
  const handleMessage = useCallback((e) => {
    if (e.data && e.data.type === 'lwc-checks') {
      setResults(e.data.results);
      setValidated(true);
      setChecking(false);
      pendingValidate.current = false;
    }
  }, []);

  React.useEffect(() => {
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [handleMessage]);

  // ── Trigger DOM checks inside iframe via postMessage ──────────────────────
  const doChecks = useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe || !iframe.contentWindow) return;
    iframe.contentWindow.postMessage({ type: 'run-checks' }, '*');
  }, []);

  const handleIframeLoad = useCallback(() => {
    if (pendingValidate.current) setTimeout(doChecks, 150);
  }, [doChecks]);

  // ── Org validation for integration challenges ─────────────────────────────
  const validateWithOrg = useCallback(async () => {
    setChecking(true);
    setValidated(false);
    try {
      const res = await fetch(`${BACKEND}/validate-lwc-org`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: sfToken, instance: sfInstance, jsCode, htmlCode, componentName, challengeId, userId }),
      });
      const data = await res.json();
      setResults((data.testResults || []).map(t => ({ description: t.methodName, pass: t.outcome === 'Pass', message: t.message || null })));
      setValidated(true);
    } catch (e) {
      setResults([{ description: 'Org validation', pass: false, message: e.message }]);
      setValidated(true);
    } finally {
      setChecking(false);
    }
  }, [sfToken, sfInstance, jsCode, htmlCode, componentName, challengeId, userId]);

  const handlePreview  = useCallback(() => compile(false), [compile]);
  const handleValidate = useCallback(() => {
    setChecking(true);
    if (isLwcIntegration && sfToken && sfInstance) {
      compile(false).then(() => validateWithOrg());
    } else {
      compile(true);
    }
  }, [compile, validateWithOrg, isLwcIntegration, sfToken, sfInstance]);

  const passed    = results.filter(r => r.pass).length;
  const total     = results.length;
  const allPassed = validated && total > 0 && passed === total;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: c.bgAlt, borderBottom: `1px solid ${c.border}`, flexShrink: 0 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: c.secondary }}>
          ⚡ Preview {mockData && <span style={{ color: '#a78bfa', fontSize: 10, marginLeft: 4 }}>mock data</span>}
        </span>
        <button onClick={handlePreview} disabled={compiling}
          style={{ padding: '5px 14px', background: c.card, border: `1px solid ${c.border}`, borderRadius: 6, color: c.text, fontSize: 12, fontWeight: 600, cursor: compiling ? 'wait' : 'pointer', opacity: compiling ? 0.6 : 1 }}>
          {compiling ? '⏳' : '▶ Run'}
        </button>
        <button onClick={handleValidate} disabled={compiling || checking}
          style={{ padding: '5px 14px', background: allPassed ? '#10b981' : c.accent, border: 'none', borderRadius: 6, color: '#fff', fontSize: 12, fontWeight: 700, cursor: (compiling || checking) ? 'wait' : 'pointer', opacity: (compiling || checking) ? 0.7 : 1 }}>
          {checking ? '⏳ Checking…' : allPassed ? '✅ Passed' : isLwcIntegration ? '✓ Validate (Org)' : '✓ Validate'}
        </button>
        {validated && (
          <span style={{ fontSize: 12, fontWeight: 700, color: allPassed ? '#10b981' : '#f87171' }}>
            {passed}/{total} passed
          </span>
        )}
      </div>

      {/* Compile error */}
      {compileErr && (
        <div style={{ padding: '8px 12px', background: '#fef0f0', borderBottom: `1px solid #f87171`, fontSize: 12, color: '#b91c1c', fontFamily: 'monospace', flexShrink: 0 }}>
          ⚠️ {compileErr}
        </div>
      )}

      {/* iframe */}
      <div style={{ flex: 1, minHeight: 0 }}>
        {srcDoc ? (
          <iframe
            key={previewKey}
            ref={iframeRef}
            srcDoc={srcDoc}
            title="LWC Preview"
            sandbox="allow-scripts"
            onLoad={handleIframeLoad}
            style={{ width: '100%', height: '100%', border: 'none', background: '#fff' }}
          />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 8, color: c.secondary }}>
            <span style={{ fontSize: 28 }}>⚡</span>
            <span style={{ fontSize: 13 }}>Click <strong>▶ Run</strong> to preview your component</span>
          </div>
        )}
      </div>

      {/* Results */}
      {validated && results.length > 0 && (
        <div style={{ flexShrink: 0, borderTop: `1px solid ${c.border}`, background: c.card, maxHeight: 180, overflowY: 'auto' }}>
          <div style={{ padding: '7px 12px', background: allPassed ? 'rgba(16,185,129,0.1)' : 'rgba(248,113,113,0.08)', borderBottom: `1px solid ${c.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: allPassed ? '#10b981' : '#f87171' }}>
              {allPassed ? '🎉 All checks passed!' : `${total - passed} check${total - passed !== 1 ? 's' : ''} failing`}
            </span>
            <div style={{ height: 5, width: 72, background: c.border, borderRadius: 3, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${total > 0 ? (passed / total) * 100 : 0}%`, background: allPassed ? '#10b981' : '#f59e0b', borderRadius: 3, transition: 'width 0.3s' }} />
            </div>
          </div>
          {results.map((r, i) => (
            <div key={i} style={{ padding: '6px 14px', borderBottom: i < results.length - 1 ? `1px solid ${c.border}` : 'none', display: 'flex', gap: 8 }}>
              <span style={{ fontSize: 13, flexShrink: 0 }}>{r.pass ? '✅' : '❌'}</span>
              <div>
                <div style={{ fontSize: 12, color: c.text, fontWeight: r.pass ? 400 : 600 }}>{r.description}</div>
                {!r.pass && r.message && (
                  <div style={{ fontSize: 11, color: '#f87171', marginTop: 2, fontFamily: 'monospace' }}>{r.message}</div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}